import { CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule } from '@angular/material/stepper';
import { BarPosition, ProgressStepIndicatorComponent, ProgressValues } from '@pc/progress-step-indicator';
import { NavigationService } from '@pc/shared';
import { VerticalStepperComponent } from '@pc/vertical-stepper';
import { ProgressStepperService } from './progress-stepper.service';

@Component({
  selector: 'pc-progress-stepper',
  standalone: true,
  imports: [
    CommonModule,
    ProgressStepIndicatorComponent,
    MatStepperModule,
    MatButtonModule,
    CdkStepperModule,
    VerticalStepperComponent
  ],
  templateUrl: './progress-stepper.component.html',
  styleUrl: './progress-stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: CdkStepper,
      useExisting: forwardRef(() => ProgressStepperComponent)
    }
  ]
})
export class ProgressStepperComponent extends CdkStepper implements OnInit, OnDestroy {
  readonly progressStepperService = inject(ProgressStepperService);
  readonly navigationService = inject(NavigationService);
  readonly destroyRef = inject(DestroyRef);
  readonly cdr = inject(ChangeDetectorRef);
  public isMobile = input<boolean>(false);
  public isDevMode = input<boolean>(false);
  public accessibilityStatementText = input<string>('');
  public headerIcon = input<string>('');
  public allowNavigation = input(true);
  public completedPercentageTranslate = input<string | undefined>(undefined);
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  public onAccessibilityStatementClicked = output<void>();

  public progress: ProgressValues = ProgressValues.Zero;

  public totalProgress = 0;

  readonly BarPosition = BarPosition;
  readonly ProgressValues = ProgressValues;

  public override get selectedIndex(): number {
    return super.selectedIndex;
  }

  public override set selectedIndex(index: number) {
    super.selectedIndex = index;
    this.progressStepperService.emitSelectedIndexUpdate(index);
  }

  public override next(): void {
    super.next();
    this.navigateToStep();
  }

  public override previous(): void {
    super.previous();
    this.navigateToStep();
  }

  ngOnInit(): void {
    this.subscribeToServiceEvents();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public isStepClickable = (selectedStepIndex: number): boolean => {
    const isInvalidStepSelected = selectedStepIndex >= this.selectedIndex && !this.isDevMode();

    return this.allowNavigation() && !isInvalidStepSelected;
  };

  public onStepClicked(selectedStepIndex: number): void {
    if (!this.isStepClickable(selectedStepIndex)) return;
    this.onStepChange(selectedStepIndex);
  }

  public onStepChange(selectedStepIndex: number): void {
    this.selectedIndex = selectedStepIndex;
    this.navigateToStep();
  }

  public accessibilityStatementClicked(): void {
    this.onAccessibilityStatementClicked.emit();
  }

  private subscribeToServiceEvents(): void {
    this.progressStepperService.progress$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((progress: ProgressValues) => this.updateProgress(progress));
    this.progressStepperService.onNext$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.next());
    this.progressStepperService.onPrevious$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.previous());
    this.progressStepperService.onNavigateToStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((stepIndex: number) => this.onStepChange(stepIndex));
  }

  private updateProgress(progress?: ProgressValues): void {
    if (this.progressStepperService.progressSteps.length === 0) return;
    const currentStep = this.progressStepperService.progressSteps[this.selectedIndex];
    this.progress = progress || currentStep.progress;
    this.totalProgress = this.calculateTotalProgress();

    this.cdr.detectChanges();
  }

  private navigateToStep(): void {
    if (this.progressStepperService.progressSteps.length === 0) return;
    const pathToNavigate = this.progressStepperService.progressSteps[this.selectedIndex].route;

    this.navigationService.navigate([pathToNavigate]);

    this.updateProgress();
  }

  private calculateTotalProgress(): number {
    const steps = this.progressStepperService.progressSteps.length;
    const stepWeight = Math.round(100 / steps);

    const stepBasePercentage = this.selectedIndex * stepWeight;
    const subStepPercentage = Math.round((this.progress / 100) * stepWeight);

    const totalProgress = stepBasePercentage + subStepPercentage;

    return totalProgress < 100 ? totalProgress : 100;
  }
}
